import { useEffect, useState } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import {
    Trash2,
    Plus,
    Loader2,
    Save,
    X,
    Pencil,
    Eye,
    EyeOff,
    Puzzle,
} from "lucide-react";

const EMPTY = {
    id: null,
    name: "",
    description: "",
    version: "",
    file_size: "-",
    requirements: "",
    changelog: "",
    image_url: "",
    download_url: "",
    download_type: "external",
    accent_color: "#34d399",
    enabled: true,
    order: 0,
};

const COLOR_PRESETS = [
    "#34d399", // zielony
    "#facc15", // żółty
    "#60a5fa", // niebieski
    "#f472b6", // różowy
    "#f97316", // pomarańczowy
    "#a78bfa", // fioletowy
    "#f87171", // czerwony
    "#2dd4bf", // turkusowy
];

export default function AddonsManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [form, setForm] = useState({ ...EMPTY });
    const [imageFile, setImageFile] = useState(null);
    const [downloadFile, setDownloadFile] = useState(null);

    const isEdit = !!form.id;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/addons");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const set = (k) => (e) => {
        const v = e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const resetForm = () => {
        setForm({ ...EMPTY });
        setImageFile(null);
        setDownloadFile(null);
    };

    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        if (!form.name.trim()) {
            setError("Podaj nazwę dodatku.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (imageFile) {
                const up = await uploadFile(imageFile);
                payload.image_url = up.url;
            }
            if (downloadFile) {
                const up = await uploadFile(downloadFile);
                payload.download_url = up.url;
                payload.download_type = "file";
                const sizeMB = up.size / (1024 * 1024);
                payload.file_size =
                    sizeMB >= 1
                        ? `${sizeMB.toFixed(1)} MB`
                        : `${(up.size / 1024).toFixed(0)} KB`;
            }
            payload.order = parseInt(payload.order || 0, 10);

            if (isEdit) {
                const { id, created_at, ...body } = payload;
                await api.put(`/addons/${form.id}`, body);
                setSuccess("Zaktualizowano dodatek.");
            } else {
                const { id, created_at, ...body } = payload;
                await api.post("/addons", body);
                setSuccess("Dodano dodatek.");
            }
            resetForm();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onEdit = (addon) => {
        setForm({
            id: addon.id,
            name: addon.name || "",
            description: addon.description || "",
            version: addon.version || "",
            file_size: addon.file_size || "-",
            requirements: addon.requirements || "",
            changelog: addon.changelog || "",
            image_url: addon.image_url || "",
            download_url: addon.download_url || "",
            download_type: addon.download_type || "external",
            accent_color: addon.accent_color || "#34d399",
            enabled: addon.enabled !== false,
            order: addon.order ?? 0,
        });
        setImageFile(null);
        setDownloadFile(null);
        setError("");
        setSuccess("");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć dodatek?")) return;
        try {
            await api.delete(`/addons/${id}`);
            if (form.id === id) resetForm();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const toggleEnabled = async (addon) => {
        try {
            await api.put(`/addons/${addon.id}`, { enabled: !(addon.enabled !== false) });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-addons" className="grid lg:grid-cols-12 gap-8">
            <form
                onSubmit={onSubmit}
                data-testid="admin-addons-form"
                className="panel-yellow p-6 lg:col-span-5 h-fit"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="label-eyebrow flex items-center gap-2">
                        <Puzzle size={13} className="text-[#34d399]" />
                        {isEdit ? "Edytuj dodatek" : "Nowy dodatek"}
                    </div>
                    {isEdit && (
                        <button
                            type="button"
                            data-testid="addon-cancel-edit"
                            onClick={resetForm}
                            className="text-xs text-[color:var(--text-secondary)] hover:text-white inline-flex items-center gap-1"
                        >
                            <X size={14} /> Anuluj
                        </button>
                    )}
                </div>

                <Field label="Nazwa *">
                    <input
                        data-testid="addon-name-input"
                        value={form.name}
                        onChange={set("name")}
                        className="input"
                    />
                </Field>
                <Field label="Opis">
                    <textarea
                        data-testid="addon-description-input"
                        value={form.description}
                        onChange={set("description")}
                        rows={3}
                        className="input"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Wersja">
                        <input
                            data-testid="addon-version-input"
                            value={form.version}
                            onChange={set("version")}
                            className="input"
                        />
                    </Field>
                    <Field label="Rozmiar">
                        <input
                            data-testid="addon-filesize-input"
                            value={form.file_size}
                            onChange={set("file_size")}
                            className="input"
                        />
                    </Field>
                </div>
                <Field label="Wymagania">
                    <input
                        data-testid="addon-requirements-input"
                        value={form.requirements}
                        onChange={set("requirements")}
                        className="input"
                    />
                </Field>
                <Field label="Changelog (opcjonalnie)">
                    <textarea
                        data-testid="addon-changelog-input"
                        value={form.changelog}
                        onChange={set("changelog")}
                        rows={3}
                        className="input font-mono text-xs"
                    />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Typ pobierania">
                        <select
                            data-testid="addon-download-type"
                            value={form.download_type}
                            onChange={set("download_type")}
                            className="input"
                        >
                            <option value="external">Link zewnętrzny</option>
                            <option value="file">Plik (baza danych)</option>
                        </select>
                    </Field>
                    <Field label="Kolejność">
                        <input
                            type="number"
                            data-testid="addon-order-input"
                            value={form.order}
                            onChange={set("order")}
                            className="input"
                        />
                    </Field>
                </div>
                <Field label="URL / ścieżka do pobrania">
                    <input
                        data-testid="addon-download-url"
                        value={form.download_url}
                        onChange={set("download_url")}
                        placeholder="https://... lub /api/media/..."
                        className="input"
                    />
                </Field>
                <Field label="Lub prześlij plik dodatku (zastąpi link)">
                    <input
                        type="file"
                        data-testid="addon-download-file"
                        onChange={(e) => setDownloadFile(e.target.files?.[0] || null)}
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                </Field>

                <Field label="Obrazek dodatku (URL lub plik)">
                    <div className="flex items-center gap-3 mb-2">
                        {form.image_url && (
                            <img
                                src={resolveMediaUrl(form.image_url)}
                                alt="Podgląd"
                                className="h-12 w-20 object-cover rounded-md border border-white/10"
                            />
                        )}
                        <input
                            data-testid="addon-image-url"
                            value={form.image_url}
                            onChange={set("image_url")}
                            placeholder="https://... lub /api/media/..."
                            className="input"
                        />
                    </div>
                    <input
                        type="file"
                        accept="image/*"
                        data-testid="addon-image-file"
                        onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                </Field>

                <label className="block mb-4">
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Kolor sekcji dodatku
                    </span>
                    <div className="flex items-center gap-3 mb-2">
                        <input
                            type="color"
                            data-testid="addon-color-input"
                            value={form.accent_color || "#34d399"}
                            onChange={set("accent_color")}
                            className="h-9 w-12 rounded-md bg-transparent border border-white/10 cursor-pointer p-0.5"
                        />
                        <input
                            data-testid="addon-color-hex"
                            value={form.accent_color || ""}
                            onChange={set("accent_color")}
                            placeholder="#34d399"
                            className="input"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {COLOR_PRESETS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`addon-color-preset-${c.replace("#", "")}`}
                                onClick={() => setForm((f) => ({ ...f, accent_color: c }))}
                                className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                                    (form.accent_color || "").toLowerCase() === c.toLowerCase()
                                        ? "border-white"
                                        : "border-transparent"
                                }`}
                                style={{ background: c }}
                                aria-label={`Kolor ${c}`}
                            />
                        ))}
                    </div>
                </label>

                <label className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] mb-4">
                    <input
                        type="checkbox"
                        data-testid="addon-enabled-input"
                        checked={form.enabled}
                        onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    />
                    Pokaż na stronie
                </label>

                {error && (
                    <div className="text-sm text-red-400 mb-3" data-testid="addon-error">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="text-sm text-[color:var(--primary)] mb-3" data-testid="addon-success">
                        {success}
                    </div>
                )}

                <button
                    data-testid="addon-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="animate-spin" size={16} />
                    ) : isEdit ? (
                        <Save size={16} />
                    ) : (
                        <Plus size={16} />
                    )}
                    {isEdit ? "Zapisz zmiany" : "Dodaj dodatek"}
                </button>

                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-7">
                <div className="label-eyebrow mb-4">Wszystkie dodatki ({items.length})</div>
                {loading ? (
                    <div className="text-[color:var(--text-secondary)]">
                        <Loader2 className="animate-spin inline" /> Ładowanie...
                    </div>
                ) : items.length === 0 ? (
                    <div className="panel-yellow p-8 text-center text-[color:var(--text-secondary)]">
                        Brak dodatków. Dodaj pierwszy po lewej.
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {items.map((addon) => (
                            <div
                                key={addon.id}
                                data-testid={`addon-row-${addon.id}`}
                                className="panel-yellow p-4 flex gap-4"
                            >
                                <div className="w-24 h-24 rounded-md overflow-hidden bg-[color:var(--bg-base)] border border-white/10 shrink-0 flex items-center justify-center">
                                    {addon.image_url ? (
                                        <img
                                            src={resolveMediaUrl(addon.image_url)}
                                            alt={addon.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Puzzle size={26} className="text-[color:var(--text-muted)]" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-3 h-3 rounded-full shrink-0 border border-white/20"
                                            style={{ background: addon.accent_color || "#34d399" }}
                                            title={addon.accent_color || "#34d399"}
                                        />
                                        <div className="text-white font-medium truncate">
                                            {addon.name}
                                        </div>
                                        {addon.enabled === false && (
                                            <span className="text-[10px] uppercase tracking-wider text-red-400 border border-red-400/40 rounded px-1.5 py-0.5">
                                                Ukryty
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-[color:var(--text-secondary)] line-clamp-2 mt-0.5">
                                        {addon.description || "-"}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-2 flex flex-wrap gap-x-3 gap-y-1">
                                        <span>v {addon.version || "-"}</span>
                                        <span>{addon.file_size || "-"}</span>
                                        <span>Kolejność: {addon.order ?? 0}</span>
                                        <span>{addon.download_url ? "Plik OK" : "Brak pliku"}</span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    <button
                                        data-testid={`addon-toggle-${addon.id}`}
                                        onClick={() => toggleEnabled(addon)}
                                        className="text-[color:var(--text-secondary)] hover:text-white p-2"
                                        title={addon.enabled !== false ? "Ukryj" : "Pokaż"}
                                    >
                                        {addon.enabled !== false ? (
                                            <Eye size={16} />
                                        ) : (
                                            <EyeOff size={16} />
                                        )}
                                    </button>
                                    <button
                                        data-testid={`addon-edit-${addon.id}`}
                                        onClick={() => onEdit(addon)}
                                        className="text-[color:var(--primary)] hover:opacity-80 p-2"
                                        title="Edytuj"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        data-testid={`addon-delete-${addon.id}`}
                                        onClick={() => onDelete(addon.id)}
                                        className="text-red-400 hover:text-red-300 p-2"
                                        title="Usuń"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-4">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
